import {gmAPI, MapService} from '@/services/MapService';
import {geohashForLocation} from 'geofire-common';

/**
 * @typedef {import('@/services/GraphicService/ImageGraphic/ImageGraphic.js').ImageGraphicData} ImageGraphicData
 */

/**
 * Given an input file, calculates width and height of the image
 * @param {File} inputFile
 * @return {Promise<{width: number, height: number}>}
 */
const getImageDimensions = async (inputFile) => {
  const dimensionsPromise = new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({width: img.width, height: img.height});
    };
    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };
    img.src = URL.createObjectURL(inputFile);
  });
  return await dimensionsPromise;
};

/**
 * Given a width and height, returns bounds on the map
 * @param {number} width
 * @param {number} height
 * @return {Promise<gmAPI.LatLngBounds>}
 */
const calculateBounds = (width, height) => {
  // If the width and height are larger than the current map resolution, reduce the image size
  const mapNode = document.getElementById('map');
  const mapHeight = mapNode.offsetHeight;
  const mapWidth = mapNode.offsetWidth;
  const reduceResolution = (w, h) => {
    if (w > mapWidth || h > mapHeight) {
      return reduceResolution(w / 2, h / 2);
    }
    return [w, h];
  };
  const [reducedWidth, reducedHeight] = reduceResolution(width, height);

  // get the center of the screen and convert to screen pixels
  const center = MapService.getMap().getCenter();
  const screenPixelLocation = MapService.latLngToScreenPixel(center);

  // calculate the latlngbounds of the image
  const llb = new gmAPI.LatLngBounds();
  for (let i = 0; i < 4; i++) {
    // if i is even, x is the left side of the image, otherwise it's the right side
    const x = i % 2 === 0 ? screenPixelLocation.x - reducedWidth / 2 : screenPixelLocation.x + reducedWidth / 2;
    // if i is less than 2, y is the top of the image, otherwise it's the bottom
    const y = i < 2 ? screenPixelLocation.y - reducedHeight / 2 : screenPixelLocation.y + reducedHeight / 2;
    const latLng = MapService.screenPixelToLatLng(x, y);
    llb.extend(latLng);
  }
  return llb;
};

/**
 * Given an input file, gets the base64, a file path, and a name for the image
 * @param {File} inputFile
 * @return {Promise<{image: string, path: string, name: string}>}
 */
const getImageData = async (inputFile) => {
  const loadPromise = new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      resolve(reader.result);
    }, false);
    reader.addEventListener('error', () => {
      reject(new Error('Failed to load image'));
    }, false);
    reader.readAsDataURL(inputFile);
  });
  const image = await loadPromise;
  return {
    image: image.split(',')[1],
    path: `files/${inputFile.name}`,
    name: inputFile.name.replace(/\.[^/.]+$/, ''),
  };
};

/**
 * Using an input file, returns feature data for an image
 * @param {File} inputFile
 */
const importImage = async (inputFile) => {
  const {width, height} = await getImageDimensions(inputFile);
  const bounds = calculateBounds(width, height);
  const center = bounds.getCenter();
  const geohash = geohashForLocation([center.lat(), center.lng()]);
  const imageData = await getImageData(inputFile);

  /** @type {ImageGraphicData} */
  const graphicData = {
    featureType: 'png',
    bounds: bounds.toJSON(),
    image: imageData.image,
    imagePath: imageData.path,
  };
  return {
    bounds: bounds.toJSON(),
    geohash,
    graphicData,
    name: imageData.name ?? 'Unknown Image',
    displayFields: [],
  };
};

export {
  importImage,
};
